import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import { Formik, getIn, FieldArray } from 'formik';
import NumberFormat from 'react-number-format';
import { array, object, string } from 'yup';
import { InputFeedback } from '../../components/UI/TextField';
import { hideLoading, showLoading } from '../../redux/loader/LoaderActions';
import { POST } from '../../services/rest.service';
import { showModal } from '../../redux/error/ErrorAction';
import { getErrorClassNames } from '../../helpers/utils';
import { OTHER_OPTION } from '../../helpers/constants';

const JobApproveModal = ({ isOpen, toggle, jobDetails, onApprove }) => {
  const dispatch = useDispatch();
  const serviceData = useSelector((state) => state.enumData);
  const [isShow, setIsshow] = useState(0);

  // A transport-only job (clearing_status 11) has no terminal, and the API
  // guard is scoped to skip it. Asking here would make it unapprovable.
  const isClearingApproval = jobDetails.clearing_status != '11';
  useEffect(() => {
    if (jobDetails?.job_type_tags) {
      jobDetails?.job_type_tags[0] === 'forwarding' && setIsshow(1);
    }
  }, [jobDetails]);
  const handleSubmit = (values) => {
    // delete the job_id field added earlier for displaying in form
    values.minimum_bid_amounts.forEach((i) => delete i.job_id);
    const payload = {
      job_uuid: jobDetails.uuid,
      job_props: values.minimum_bid_amounts,
      approved_at: new Date().toISOString(), // Add the approved_at timestamp
    };

    // Sent only for clearing approvals. The API refuses the approval outright
    // if the job has neither these nor its own stored values.
    if (isClearingApproval) {
      // Split the same way the importer's form does: a uuid or a typed name,
      // never both. The API clears whichever side it was not given, so an
      // operator correcting an importer's typed value to a real terminal
      // leaves no stale text behind.
      const terminalIsOther = values.terminal_uuid === OTHER_OPTION;
      const lineIsOther = values.shipping_line_uuid === OTHER_OPTION;

      payload.terminal_uuid = terminalIsOther ? '' : values.terminal_uuid || '';
      payload.terminal_other = terminalIsOther ? values.terminal_other || '' : '';
      payload.shipping_line_uuid = lineIsOther ? '' : values.shipping_line_uuid || '';
      payload.shipping_line_other = lineIsOther ? values.shipping_line_other || '' : '';
    }

    dispatch(showLoading());
    POST(`/jobs/operatorApproveJob`, payload)
      .then(({ data }) => {
        // console.log('data', data);
        let jobData = { ...jobDetails, clearing_status: data.job.clearing_status };
        dispatch(hideLoading());
        dispatch(showModal(`Service approved successfully!`, true));
        onApprove(jobData);
      })
      .catch((err) => {
        dispatch(hideLoading());
        dispatch(showModal(err.message));
      });
  };

  // minimum bid amounts for each job in a service requrest.
  const minimumBidAmounts = [];

  // for job types vehicle-container and general cargo, we will have containers from which transport jobs are created
  // this is for the transport part of the job
  if (jobDetails.children_packages?.length > 0) {
    jobDetails.children_packages.forEach((cp) => {
      minimumBidAmounts.push({
        job_id: cp.job_id,
        uuid: cp.uuid,
        minimum_bid_amount: '',
        type: 'Transport',
      });
    });
  }

  // this is for the clearing part of the job (is not asked for transport-only job)
  if (jobDetails.clearing_status != '11') {
    minimumBidAmounts.push({
      job_id: jobDetails.job_id,
      uuid: jobDetails.uuid,
      minimum_bid_amount: '',
      type: 'Clearing',
    });
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} container="client-page" className="modal-md">
      <div className="modal-header">
        <button type="button" className="close" onClick={toggle}>
          &times;
        </button>
      </div>
      <ModalBody>
        <div className="reson-reject">
          <h5>Approval</h5>
          <Formik
            initialValues={{
              minimum_bid_amounts: minimumBidAmounts,
              // Prefilled from the job when the importer supplied them, so the
              // operator confirms rather than re-enters.
              // A job posted with "Others" has no uuid. Opening on a blank
              // select would hide what the importer actually wrote and invite
              // the operator to overwrite it without ever seeing it.
              terminal_uuid:
                jobDetails.terminal_uuid || (jobDetails.terminal_other ? OTHER_OPTION : ''),
              terminal_other: jobDetails.terminal_other || '',
              shipping_line_uuid:
                jobDetails.shipping_line_uuid ||
                (jobDetails.shipping_line_other ? OTHER_OPTION : ''),
              shipping_line_other: jobDetails.shipping_line_other || '',
            }}
            onSubmit={handleSubmit}
            validationSchema={object().shape({
              minimum_bid_amounts: array().of(
                object().shape({
                  minimum_bid_amount: string().required('Minimum Bid Amount is required'),
                }),
              ),
              terminal_uuid: isClearingApproval
                ? string().required('Terminal is required before this job can go to bidding')
                : string().nullable(),
              shipping_line_uuid: isClearingApproval
                ? string().required('Shipping Line is required before this job can go to bidding')
                : string().nullable(),
              terminal_other: string().when('terminal_uuid', (terminalUuid, schema) =>
                terminalUuid === OTHER_OPTION
                  ? schema.required('Please enter the terminal name').max(120)
                  : schema.nullable(),
              ),
              shipping_line_other: string().when('shipping_line_uuid', (lineUuid, schema) =>
                lineUuid === OTHER_OPTION
                  ? schema.required('Please enter the shipping line name').max(120)
                  : schema.nullable(),
              ),
            })}
          >
            {(renderProps) => {
              const {
                values,
                touched,
                errors,
                setFieldValue,
                handleSubmit,
                handleChange,
                handleBlur,
              } = renderProps;

              // console.log('>> values, touched, errors,', values, touched, errors);
              return (
                <form className={'row'} onSubmit={handleSubmit}>
                  <Fragment>
                    {isClearingApproval && (
                      <>
                        <div className="col-sm-12 text-left">
                          <label>
                            Terminal <span className="text-danger">*</span>
                          </label>
                          <select
                            className={getErrorClassNames(
                              getIn(touched, 'terminal_uuid') && getIn(errors, 'terminal_uuid'),
                            )}
                            name="terminal_uuid"
                            id="terminal_uuid"
                            value={values.terminal_uuid}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Select Terminal</option>
                            {serviceData?.terminals?.length > 0 &&
                              serviceData.terminals
                                .filter(
                                  (t) =>
                                    !jobDetails.clearing_discharge_port ||
                                    t.port_uuid === jobDetails.clearing_discharge_port,
                                )
                                .map((t) => (
                                  <option value={t.uuid} key={t.uuid}>
                                    {t.terminal_name}
                                  </option>
                                ))}
                            <option value={OTHER_OPTION}>Others (type it in)</option>
                          </select>
                          <InputFeedback
                            error={
                              getIn(touched, 'terminal_uuid') && getIn(errors, 'terminal_uuid')
                            }
                          />
                          {values.terminal_uuid === OTHER_OPTION && (
                            <div className="mt-2">
                              <input
                                type="text"
                                className={getErrorClassNames(
                                  getIn(touched, 'terminal_other') &&
                                    getIn(errors, 'terminal_other'),
                                )}
                                name="terminal_other"
                                id="terminal_other"
                                maxLength={120}
                                placeholder="Enter the terminal name"
                                value={values.terminal_other}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <InputFeedback
                                error={
                                  getIn(touched, 'terminal_other') &&
                                  getIn(errors, 'terminal_other')
                                }
                              />
                            </div>
                          )}
                        </div>

                        <div className="col-sm-12 text-left">
                          <label>
                            Shipping Line <span className="text-danger">*</span>
                          </label>
                          <select
                            className={getErrorClassNames(
                              getIn(touched, 'shipping_line_uuid') &&
                                getIn(errors, 'shipping_line_uuid'),
                            )}
                            name="shipping_line_uuid"
                            id="shipping_line_uuid"
                            value={values.shipping_line_uuid}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Select Shipping Line</option>
                            {serviceData?.shipping_lines?.length > 0 &&
                              serviceData.shipping_lines.map((s) => (
                                <option value={s.uuid} key={s.uuid}>
                                  {s.shipping_line_name}
                                </option>
                              ))}
                            <option value={OTHER_OPTION}>Others (type it in)</option>
                          </select>
                          <InputFeedback
                            error={
                              getIn(touched, 'shipping_line_uuid') &&
                              getIn(errors, 'shipping_line_uuid')
                            }
                          />
                          {values.shipping_line_uuid === OTHER_OPTION && (
                            <div className="mt-2">
                              <input
                                type="text"
                                className={getErrorClassNames(
                                  getIn(touched, 'shipping_line_other') &&
                                    getIn(errors, 'shipping_line_other'),
                                )}
                                name="shipping_line_other"
                                id="shipping_line_other"
                                maxLength={120}
                                placeholder="Enter the shipping line name"
                                value={values.shipping_line_other}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <InputFeedback
                                error={
                                  getIn(touched, 'shipping_line_other') &&
                                  getIn(errors, 'shipping_line_other')
                                }
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    <FieldArray
                      name="minimum_bid_amounts"
                      render={(arrayHelpers) => (
                        <>
                          {values.minimum_bid_amounts?.length > 0 &&
                            values.minimum_bid_amounts.map((job, cpIndex) => {
                              const fieldName = `minimum_bid_amounts.${cpIndex}.minimum_bid_amount`;
                              return (
                                <div className="col-sm-12 text-left" key={job.job_uuid}>
                                  <label>
                                    Minimum Bid Amount for <strong>{` ${job.type} `}</strong> Job
                                    (ID): {job.job_id}
                                  </label>
                                  <NumberFormat
                                    thousandsGroupStyle="thousand"
                                    decimalSeparator="."
                                    value={job.minimum_bid_amount}
                                    className={getErrorClassNames(
                                      getIn(touched, fieldName) && getIn(errors, fieldName),
                                    )}
                                    displayType="input"
                                    type="text"
                                    thousandSeparator={true}
                                    prefix=""
                                    id={fieldName}
                                    name={fieldName}
                                    onValueChange={(e) => setFieldValue(fieldName, e.value)}
                                  />
                                  <InputFeedback
                                    error={getIn(touched, fieldName) && getIn(errors, fieldName)}
                                  />
                                </div>
                              );
                            })}
                        </>
                      )}
                    />
                  </Fragment>

                  <div className="buttonsec1" style={{ textAlign: 'center' }}>
                    <button type="submit" className="submitbut">
                      Submit
                    </button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default JobApproveModal;
